import React from 'react';
import {
  AppBar,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Icon,
  IconButton,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import { tokens } from '../../theme/tokens';
import { V4_DIALOG_CONTENT, V4_ICON_BUTTON, V4_SMALL_SCREEN } from '../../theme/v4Compat';
import Alert from '@mui/material/Alert';
import { extractApiErrorMessage, notifications } from '../../utils';
import { MedicalSuppliesService } from '../../hooks/MedicalSuppliesService';
import { LoaderSize } from '../../constants/constants';
import { FarmMedicalSupplySelect, Loader } from '../../elements';
import { FarmMedicalSupplyViewModel } from '../../models/injection';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyringe } from '@fortawesome/free-solid-svg-icons';

export interface LocationVaccinationModalResult {
  farmSupplyId: string;
  repeatDays: number;
  dose: number;
}

interface LocationVaccinationModalProps {
  opened: boolean;
  onModalAction: (result: LocationVaccinationModalResult | null) => void;
}

const LocationVaccinationModal: React.FC<any> = ({ opened, onModalAction }: LocationVaccinationModalProps) => {
  const { t } = useTranslation();
  const fullScreen = useMediaQuery(V4_SMALL_SCREEN);

  const medicalSuppliesService = new MedicalSuppliesService();

  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);

  const [farmSupplies, setFarmSupplies] = React.useState([] as FarmMedicalSupplyViewModel[]);
  const [repeatDays, setRepeatDays] = React.useState(0);
  const [dose, setDose] = React.useState(0);
  const [farmSupplyId, setFarmSupplyId] = React.useState('');
  const [giltsConfirmed, setGiltsConfirmed] = React.useState(false);

  React.useEffect(() => {
    if (opened) {
      // Require the gilts-only confirmation to be made deliberately each time.
      setGiltsConfirmed(false);

      if (!loaded) {
        const loadData = async () => loadFarmMedicalSupplies();
        loadData();
      }
    }
  }, [opened]);

  const loadFarmMedicalSupplies = async () => {
    setIsBusy(true);

    medicalSuppliesService
      .getFarmMedicalSupplies()
      .then((supplies) => {
        setFarmSupplies(supplies);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const closeDialog = (text: string | null) => {
    if (text == null) {
      onModalAction(null);
      return;
    }
    if (!validateForm()) {
      return;
    }

    onModalAction({ farmSupplyId, repeatDays, dose });
  };

  const validateForm = (): boolean => {
    if (repeatDays < 0) {
      notifications.warning(t('f-val-days-feed'));
      return false;
    }
    if (!farmSupplyId) {
      notifications.warning(t('f-val-farm-supply'));
      return false;
    }
    if (!giltsConfirmed) {
      notifications.warning(t('f-loc-vaccination-confirm-required'));
      return false;
    }

    return true;
  };

  const handleFarmSupplyChange = (value: string) => {
    setFarmSupplyId(value || '');
  };

  return (
    <Dialog
      id="vaccinationDialog"
      open={opened}
      onClose={() => closeDialog(null)}
      sx={{ overflow: 'hidden' }}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="form-dialog-title"
    >
      {fullScreen && (
        <AppBar>
          <Toolbar>
            <div className="flex w-full justify-between">
              <div className="flex items-center">
                <Tooltip title={t('f-com-cancel')} arrow>
                  <IconButton
                    edge="start"
                    color="inherit"
                    sx={V4_ICON_BUTTON}
                    onClick={() => closeDialog(null)}
                    aria-label="close"
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
                <Typography variant="h6">{t('f-loc-vaccination-title')}</Typography>
              </div>

              <Button
                onClick={() => closeDialog('ok')}
                id="saveVaccinationBtn"
                startIcon={<FontAwesomeIcon icon={faSyringe} />}
                color="inherit"
                disabled={!giltsConfirmed}
              >
                {t('f-loc-vaccination-action')}
              </Button>
            </div>
          </Toolbar>
        </AppBar>
      )}

      <DialogTitle id="locationVaccinationDialogTitle">{t('f-loc-vaccination-title')}</DialogTitle>

      <DialogContent sx={V4_DIALOG_CONTENT}>
        <Grid container direction="column" spacing={2} sx={{ minWidth: 250, justifyContent: 'center' }}>
          <Alert sx={{ mt: 1 }} severity="info">
            {t('f-loc-vaccination-alert')}
          </Alert>

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {loaded && (
            <>
              <Grid item xs={12}>
                <FarmMedicalSupplySelect
                  supplies={farmSupplies}
                  value={farmSupplyId}
                  fullWidth
                  onSupplyChange={handleFarmSupplyChange}
                />
              </Grid>

              <TextField
                id="days-count-input"
                label={t('f-loc-feed-days')}
                variant="outlined"
                sx={{ m: 1, minWidth: 220 }}
                value={repeatDays}
                size="small"
                type="number"
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (newValue >= 0) {
                    setRepeatDays(newValue);
                  }
                }}
              />

              <TextField
                id="dose-input"
                label={t('f-loc-feed-dose')}
                variant="outlined"
                sx={{ m: 1, minWidth: 220 }}
                value={dose}
                size="small"
                type="number"
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (newValue >= 0) {
                    setDose(newValue);
                  }
                }}
              />

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="gilts-only-confirm"
                      color="primary"
                      checked={giltsConfirmed}
                      onChange={(e) => setGiltsConfirmed(e.target.checked)}
                    />
                  }
                  label={t('f-loc-vaccination-gilts-only')}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      {!fullScreen && (
        <DialogActions sx={{ background: tokens.sidebar }}>
          <Button
            onClick={() => closeDialog(null)}
            variant="outlined"
            startIcon={<Icon>{'close'}</Icon>}
            color="primary"
          >
            {t('f-com-cancel')}
          </Button>
          <Button
            onClick={() => closeDialog('ok')}
            id="saveVaccinationBtn"
            startIcon={<FontAwesomeIcon icon={faSyringe} />}
            variant="contained"
            color="primary"
            disabled={!giltsConfirmed}
          >
            {t('f-loc-vaccination-action')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default LocationVaccinationModal;
